import React, { useContext, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useThemeObserver, token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data';
import { CoreInvitesContext, CoreInvitesSource } from '@confluence/core-invites-provider';

import { HTV2DismissButton } from './HTV2DismissButton';

const blueHeaderStyling = xcss({
	backgroundImage: `url(${require('./assets/BlueHeaderBackground.svg')})`,
});

const greenHeaderStyling = xcss({
	backgroundImage: `url(${require('./assets/GreenHeaderBackground.svg')})`,
});

const blueHeaderDarkStyling = xcss({
	backgroundImage: `url(${require('./assets/BlueHeaderDarkBackground.svg')})`,
});

const greenHeaderDarkStyling = xcss({
	backgroundImage: `url(${require('./assets/GreenHeaderDarkBackground.svg')})`,
});

const headerStyling = xcss({
	height: '144px',
	position: 'relative',
	border: `1px solid ${token('color.border')}`,
	borderRadius: '8px',
});

const containerStyles = xcss({
	height: '100%',
	width: '100%',
});

const i18n = defineMessages({
	header: {
		id: 'experiment-home-onboarding-takeover-v2.header.heading',
		defaultMessage: 'Welcome, {name}! 👋',
		description:
			'The text is shown as a header in a banner. It is used to greet the user to Confluence as onboarding. The placeholder {name} is replaced with the users first name',
	},
	dismissIconLabel: {
		id: 'experiment-home-onboarding-takeover-v2.dismiss-icon-label',
		defaultMessage: 'Dismiss banner',
		description:
			'This text is shown as a label for a dismiss/cross icon for accessibility. The icon can then be used to close/dismiss the banner',
	},
	backupHeader: {
		id: 'experiment-home-onboarding-takeover-v2.header.heading.backup',
		defaultMessage: 'Welcome! 👋',
		description:
			'The text is shown as a header in a banner. It is used to greet the user to Confluence as onboarding.',
	},
	description: {
		id: 'experiment-home-onboarding-takeover-v2.header.description',
		defaultMessage: 'How do you want to get started?',
		description:
			'The text is shown as a description in a banner. It is used to ask what onboarding tasks the user wants to do',
	},
	successHeader: {
		id: 'experiment-home-onboarding-takeover-v2.header.success-heading',
		defaultMessage: 'Congrats, {name}! 🏆',
		description:
			'The text is shown as a header in a banner. It is used to congratulate them for finishing their onboarding tasks. The placeholder {name} is replaced with the users first name',
	},
	successBackupHeader: {
		id: 'experiment-home-onboarding-takeover-v2.header.success-heading.backup',
		defaultMessage: 'Congrats! 🏆',
		description:
			'The text is shown as a header in a banner. It is used to congratulate them for finishing their onboarding tasks.',
	},
	successDescription: {
		id: 'experiment-home-onboarding-takeover-v2.header.success-description',
		defaultMessage: 'You completed your onboarding tasks',
		description:
			'The text is shown as a description in a banner. It is used to congratulate them for finishing their onboarding tasks.',
	},
	invite: {
		id: 'experiment-home-onboarding-takeover-v2.header.invite',
		defaultMessage: 'Invite people',
		description:
			'The text is shown as a text in a button. The button then triggers a modal that can be used to invite their team members',
	},
});

type HomeOnboardingTakeoverV2HeaderProps = {
	isFinished: boolean;
	onDismiss: () => void;
};

const getCopy = (isFinished: boolean, hasName: boolean) => {
	if (isFinished) {
		return {
			heading: hasName ? i18n.successHeader : i18n.successBackupHeader,
			description: i18n.successDescription,
		};
	}

	return {
		heading: hasName ? i18n.header : i18n.backupHeader,
		description: i18n.description,
	};
};

export const HTV2Header = ({ isFinished, onDismiss }: HomeOnboardingTakeoverV2HeaderProps) => {
	const { displayName } = useSessionData();
	const theme = useThemeObserver();

	const userFirstName: string = displayName?.split(' ')[0] || '';
	const isDarkMode = theme.colorMode === 'dark';

	const { heading, description } = getCopy(Boolean(isFinished), Boolean(userFirstName));

	return (
		<Box
			xcss={[
				headerStyling,
				isDarkMode
					? isFinished
						? greenHeaderDarkStyling
						: blueHeaderDarkStyling
					: isFinished
						? greenHeaderStyling
						: blueHeaderStyling,
			]}
			paddingInline="space.250"
			paddingBlock="space.500"
		>
			<Flex
				xcss={containerStyles}
				alignItems="center"
				justifyContent="space-between"
				direction="row"
			>
				<Flex direction="column" gap="space.100">
					<Heading size="xlarge">
						<FormattedMessage {...heading} values={{ name: userFirstName }} />
					</Heading>
					<Heading size="medium">
						<FormattedMessage {...description} />
					</Heading>
				</Flex>
				{isFinished ? (
					<HTV2DismissButton onDismiss={onDismiss} label={i18n.dismissIconLabel} />
				) : (
					<InviteButton />
				)}
			</Flex>
		</Box>
	);
};

const InviteButton = () => {
	const { openCoreInvites } = useContext(CoreInvitesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOpen = useCallback(() => {
		openCoreInvites?.(CoreInvitesSource.HOME_ONBOARDING_TAKEOVER_V2);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2HeaderInvite',
				source: 'onboarding',
			},
		}).fire();
	}, [openCoreInvites, createAnalyticsEvent]);

	return (
		<Button iconBefore={InviteIcon} onClick={handleOpen}>
			<FormattedMessage {...i18n.invite} />
		</Button>
	);
};

const InviteIcon = () => <PeopleGroupIcon LEGACY_size="small" label="" color="currentColor" />;
